// @ts-nocheck
/**
 * Contracts API Transformers
 * Pure field mapping functions - no business logic
 */

import {
  normalizeEntity,
  normalizeRelation,
  normalizeRelationArray,
} from '@ufc/core';

import type {
  SalesOrder,
  SalesOrderStatus,
  OrderLine,
  OrderLineStatus,
  SalesOrderStats,
  CementProduct,
} from '../domain/types';

import type { ApiSalesOrderDto, ApiSalesOrderLineDto } from './api-types';

import {
  DEFAULT_CURRENCY,
  DEFAULT_PRODUCT_NAME,
  API_STATUS_TO_DOMAIN,
} from '../domain/constants';

// ============================================
// Helper Functions
// ============================================

function mapApiStatusToDomain(apiStatus: string): SalesOrderStatus {
  return (API_STATUS_TO_DOMAIN[
    apiStatus as keyof typeof API_STATUS_TO_DOMAIN
  ] ?? 'draft') as SalesOrderStatus;
}

function inferPackaging(
  variant: Record<string, unknown> | null,
): 'bags' | 'bulk' {
  if (!variant) return 'bags';
  const haystack = `${variant.variantCode ?? variant.skuCode ?? ''} ${
    variant.name ?? ''
  }`.toLowerCase();
  if (haystack.includes('bulk') || haystack.includes('ton')) return 'bulk';
  return 'bags';
}

function containsArabicScript(value: string): boolean {
  return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(value);
}

function normalizeLocaleTag(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return undefined;
  return trimmed.split(/[-_]/)[0] || undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getFiniteNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function coerceNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function normalizeLocalizedText(value: unknown): { en: string; ar: string } {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return { ...DEFAULT_PRODUCT_NAME };

    return containsArabicScript(trimmed)
      ? { en: '', ar: trimmed }
      : { en: trimmed, ar: '' };
  }

  if (isRecord(value)) {
    return {
      en: typeof value.en === 'string' ? value.en.trim() : '',
      ar: typeof value.ar === 'string' ? value.ar.trim() : '',
    };
  }

  return { ...DEFAULT_PRODUCT_NAME };
}

function extractStrictLocalizedText(
  entity: Record<string, unknown> | null | undefined,
): { en: string; ar: string } {
  if (!entity) return { en: '', ar: '' };

  const localizedText = { en: '', ar: '' };
  const mainName = coerceNonEmptyString(entity.name);
  const mainLocale = normalizeLocaleTag(entity.locale);

  if (mainName) {
    if (mainLocale === 'en') localizedText.en = mainName;
    if (mainLocale === 'ar') localizedText.ar = mainName;
  }

  const localizations = normalizeRelationArray<Record<string, unknown>>(
    entity.localizations,
  );

  for (const localization of localizations) {
    const locale = normalizeLocaleTag(localization.locale);
    const name = coerceNonEmptyString(localization.name);
    if (!name) continue;

    if (locale === 'en') localizedText.en = name;
    if (locale === 'ar') localizedText.ar = name;
  }

  return localizedText;
}

/** Strapi location: lines + postal, else `name`. */
function buildAddressLineFromLocationRecord(
  loc: Record<string, unknown> | null | undefined,
): string {
  if (!loc) return '';
  const parts = [loc.addressLine1, loc.addressLine2, loc.postalCode].filter(
    Boolean,
  ) as string[];
  const joined = parts.join(', ');
  if (joined) return joined;
  return coerceNonEmptyString(loc.name) ?? '';
}

// ============================================
// Line Transformer
// ============================================

function transformLineToOrderLine(
  line: ApiSalesOrderLineDto,
  orderNumber: string,
  lineIndex: number,
): OrderLine {
  const metadata = (line.metadata ?? {}) as Record<string, unknown>;
  const variantData = normalizeRelation<Record<string, unknown>>(
    (line as Record<string, unknown>).variant,
  );
  const skuData = normalizeRelation<Record<string, unknown>>(variantData?.sku);
  const categories = normalizeRelationArray<Record<string, unknown>>(
    skuData?.categories,
  );

  const productCode = String(
    (categories[0] as Record<string, unknown>)?.code ??
      variantData?.variantCode ??
      variantData?.skuCode ??
      metadata.productCode ??
      '',
  ).trim();

  const variantNameLocalized = extractStrictLocalizedText(variantData);
  const variantNameValue = coerceNonEmptyString(
    variantNameLocalized.en || variantNameLocalized.ar || metadata.variantName,
  );
  const productName =
    skuData?.name ?? metadata.productName ?? variantNameLocalized.en ?? variantNameLocalized.ar ?? DEFAULT_PRODUCT_NAME;
  const productLocalized =
    skuData?.name != null
      ? normalizeLocalizedText(skuData.name)
      : variantNameLocalized.en || variantNameLocalized.ar
        ? variantNameLocalized
        : normalizeLocalizedText(productName);
  const packaging = (metadata.packaging ?? inferPackaging(variantData)) as
    | 'bags'
    | 'bulk';
  const unitPrice = (metadata.unitPrice as {
    amount: number;
    currency: string;
  }) ?? { amount: 0, currency: DEFAULT_CURRENCY };
  const unitPriceAmount = getFiniteNumber(unitPrice.amount, 0);
  const committedQuantity = getFiniteNumber(line.committedQuantity, 0);
  const canceledQuantity = getFiniteNumber(line.canceledQuantity, 0);
  const effectiveCommittedQuantity = getFiniteNumber(
    line.effectiveCommittedQuantity,
    Math.max(0, committedQuantity - canceledQuantity),
  );
  const remainingQuantity =
    typeof line.remainingQuantity === 'number'
      ? line.remainingQuantity
      : undefined;
  const actualQuantity = getFiniteNumber(
    line.actualQuantity,
    remainingQuantity != null
      ? Math.max(0, effectiveCommittedQuantity - remainingQuantity)
      : 0,
  );
  const splitBy =
    typeof line.splitBy === 'number' && Number.isFinite(line.splitBy)
      ? line.splitBy
      : line.committedQuantity;
  const unit = packaging === 'bags' ? 'bags' : 'tons';

  // Prefer variant relation's documentId; fall back to metadata.variantId (stored at order creation time)
  const variantIdValue =
    variantData?.documentId ?? variantData?.id ?? metadata.variantId;
  const deliveryOptionValue = (metadata.deliveryOption ??
    metadata.fulfillment) as 'automatic' | 'assign' | 'ufc' | undefined;

  const splitsRaw = (metadata.deliveryOptionSplits ??
    metadata.fulfillmentSplits) as unknown;
  const deliveryOptionSplits = Array.isArray(splitsRaw)
    ? (splitsRaw as unknown[]).map((split) => {
        if (!isRecord(split)) return null;
        const option = split.deliveryOption ?? split.fulfillment;
        if (option !== 'automatic' && option !== 'assign' && option !== 'ufc')
          return null;
        return {
          deliveryOption: option,
          assignee: split.assignee,
          resourceId: split.resourceId,
          resourceDocumentId: split.resourceDocumentId,
        };
      })
    : undefined;

  return {
    lineId: `${orderNumber}-line-${lineIndex + 1}`,
    strapiId: line.id,
    strapiDocumentId: line.documentId,
    variantId:
      typeof variantIdValue === 'string' || typeof variantIdValue === 'number'
        ? variantIdValue
        : undefined,
    variantName:
      typeof variantNameValue === 'string' ? variantNameValue : undefined,
    productId: String(
      skuData?.id ??
        variantData?.id ??
        metadata.productId ??
        `prod-${productCode.toLowerCase()}`,
    ),
    productCode,
    product: productLocalized,
    packaging,
    splitBy,
    status: line.status as OrderLineStatus | undefined,
    deliveryOption: deliveryOptionValue,
    assignee:
      typeof metadata.assignee === 'string' ? metadata.assignee : undefined,
    resourceId:
      metadata.resourceId != null ? String(metadata.resourceId) : undefined,
    resourceDocumentId:
      typeof metadata.resourceDocumentId === 'string'
        ? metadata.resourceDocumentId
        : undefined,
    ...(deliveryOptionSplits && deliveryOptionSplits.some(Boolean)
      ? { deliveryOptionSplits }
      : {}),
    allocatedQuantity: { value: effectiveCommittedQuantity, unit },
    usedQuantity: { value: actualQuantity, unit },
    unitPrice,
    lineTotal: {
      amount: effectiveCommittedQuantity * unitPriceAmount,
      currency: DEFAULT_CURRENCY,
    },
  };
}

// ============================================
// Sales Order Transformer
// ============================================

export function transformSalesOrderResponseToDomain(
  dto: ApiSalesOrderDto,
): SalesOrder {
  const orderNumber = dto.salesOrderNumber || `SO-${dto.id}`;
  const documentId = dto.documentId ? String(dto.documentId) : undefined;

  const lines = (dto.salesOrderLines || []).map((line, idx) =>
    transformLineToOrderLine(line, orderNumber, idx),
  );

  const totalValue = lines.reduce(
    (sum, line) => sum + line.lineTotal.amount,
    0,
  );
  const usedValue = lines.reduce(
    (sum, line) => sum + line.usedQuantity.value * (line.unitPrice.amount || 0),
    0,
  );

  const status: SalesOrderStatus = mapApiStatusToDomain(dto.status);

  const metadata = (dto.metadata ?? {}) as Record<string, unknown>;
  const paymentMethodValue = metadata.paymentMethod as
    | SalesOrder['paymentMethod']
    | undefined;

  // Extract factory info
  const factory = (dto as any).factory;
  const factoryId = factory?.documentId ?? factory?.id;
  const factoryName = factory?.name;
  const city = (dto as any).city;
  const cityId = city?.documentId ?? city?.id ?? metadata.cityId;
  const cityName =
    typeof city?.name === 'string'
      ? city.name
      : (city?.name?.en ?? city?.name?.ar ?? metadata.cityName);

  /** Populated when API includes `deliveryAddress` (location); often absent — then use city + metadata below. */
  const deliveryLoc = normalizeRelation<Record<string, unknown>>(
    (dto as Record<string, unknown>).deliveryAddress as unknown,
  );

  const deliveryAddress =
    buildAddressLineFromLocationRecord(deliveryLoc) ||
    coerceNonEmptyString(metadata.deliveryAddress) ||
    (typeof cityName === 'string' ? cityName : undefined) ||
    undefined;

  const deliveryAddressId =
    metadata.deliveryAddressId != null
      ? String(metadata.deliveryAddressId)
      : deliveryLoc?.documentId != null || deliveryLoc?.id != null
        ? String(deliveryLoc.documentId ?? deliveryLoc.id)
        : undefined;

  // Extract orders count from populated relation
  const ordersArray = dto.orders ?? [];
  const ordersCount = {
    total: ordersArray.length,
    pending: ordersArray.filter(
      (o) => o.orderStatus === 'planned' || o.orderStatus === 'processing',
    ).length,
  };

  const fallbackDate =
    dto.createdAt ||
    dto.updatedAt ||
    dto.validityEnd ||
    new Date().toISOString();
  const startDate = fallbackDate;
  const endDate = dto.validityEnd || fallbackDate;

  return {
    id: documentId ?? String(dto.id),
    strapiId: dto.id,
    strapiDocumentId: documentId,
    orderNumber,
    name: (metadata.name as { en: string; ar: string }) ?? {
      en: orderNumber,
      ar: orderNumber,
    },
    type: (metadata.type as SalesOrder['type']) ?? 'supply',
    customerId: String(metadata.customerId ?? 'unknown'),
    status,
    tenant: dto.tenant
      ? { name: dto.tenant.name, code: dto.tenant.code }
      : undefined,
    startDate,
    endDate,
    paymentMethod: paymentMethodValue,
    cityId: cityId != null ? String(cityId) : undefined,
    cityName: typeof cityName === 'string' ? cityName : undefined,
    deliveryAddressId,
    deliveryAddress,
    factoryId: factoryId != null ? String(factoryId) : undefined,
    factoryName: typeof factoryName === 'string' ? factoryName : undefined,
    totalValue: { amount: totalValue, currency: DEFAULT_CURRENCY },
    usedValue: { amount: usedValue, currency: DEFAULT_CURRENCY },
    lines,
    accountManagerId: metadata.accountManagerId as string | undefined,
    projectIds: (metadata.projectIds as string[]) ?? [],
    terms: metadata.terms as string | undefined,
    notes: metadata.notes as string | undefined,
    createdAt: dto.createdAt || fallbackDate,
    updatedAt: dto.updatedAt || dto.createdAt || fallbackDate,
    ordersCount,
    createdByUser: dto.createdByUser
      ? {
          id: dto.createdByUser.id,
          username: dto.createdByUser.username ?? undefined,
          email: dto.createdByUser.email ?? undefined,
          fullName: dto.createdByUser.fullName ?? undefined,
        }
      : undefined,
  };
}

export function transformSalesOrdersResponseToDomain(
  dtos: ApiSalesOrderDto[],
): SalesOrder[] {
  return dtos.map(transformSalesOrderResponseToDomain);
}

// ============================================
// Stats Transformer
// ============================================

export function computeStats(orders: SalesOrder[]): SalesOrderStats {
  return {
    active: orders.filter((o) => o.status === 'active').length,
    completed: orders.filter((o) => o.status === 'completed').length,
    draft: orders.filter((o) => o.status === 'draft').length,
    total: orders.length,
    totalValue: {
      amount: orders.reduce((sum, o) => sum + o.totalValue.amount, 0),
      currency: DEFAULT_CURRENCY,
    },
  };
}

// ============================================
// Legacy Aliases
// ============================================

export const transformStrapiSalesOrderToDomain =
  transformSalesOrderResponseToDomain;
export const transformStrapiSalesOrdersToDomain =
  transformSalesOrdersResponseToDomain;
